// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\visites\MissedVisitsPage.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Table, Spin, Alert, Typography, Select, Row, Col, message, Tooltip, Card, Empty, Tag,
    DatePicker, Statistic, Button, Modal, Space, List, Divider, Collapse, Badge
} from 'antd';
import {
    UserOutlined, TeamOutlined, ScheduleOutlined, MedicineBoxOutlined, WarningOutlined, 
    DownloadOutlined, AuditOutlined, RocketOutlined, EnvironmentOutlined, StarFilled,
    UnorderedListOutlined, CalendarOutlined, CrownOutlined, CheckCircleOutlined, StopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useSelector, useDispatch } from 'react-redux';
import { getMissedVisitsGrouped, exportMissedVisits, getConsecutiveMissedVisits } from '../../services/visitService';
import { getAllDelegatesList } from '../../services/delegateService';
import { fetchCycles } from '../../redux/slices/planning/planningThunks';
import { getDelegatesList } from '../../redux/slices/delegates/delegatesThunk';
import { getAllDoctorsForFilter } from '../../redux/slices/persons/personsThunk';
import { RoleEnum } from '../../_utils/roleEnum';
import axiosInstance from '../../services/axiosServicesVisit';
import DoctorHistoryModal from '../../components/DoctorHistoryModal';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

// --- CONFIGURATION DE LA SEGMENTATION RELATIONNELLE AVEC COULEURS DE LA MODALE ---
export const RELATIONSHIP_CONFIG = {
  PARTNER_ACTIVE: {
    label: "⭐ Partenaire Stratégique",
    tagColor: "gold",
    icon: <StarFilled />,
    bgColor: "#fffbe6",
    borderColor: "#fadb14",
    titleColor: "#856404",
    shadow: "0 4px 12px rgba(250, 219, 20, 0.18)"
  },
  PARTNER_AT_RISK: {
    label: "⚠️ Partenaire Sous Vigilance",
    tagColor: "warning",
    icon: <WarningOutlined />,
    bgColor: "#fff7e6",
    borderColor: "#fa8c16",
    titleColor: "#d46b08",
    shadow: "0 4px 12px rgba(250, 140, 22, 0.18)"
  },
  KEY_PRESCRIBER: {
    label: "🚀 Prescripteur Clé (A+)",
    tagColor: "success",
    icon: <RocketOutlined />,
    bgColor: "#f6ffed",
    borderColor: "#52c41a",
    titleColor: "#237804",
    shadow: "0 4px 12px rgba(82, 196, 26, 0.18)"
  },
  STANDARD: {
    label: "👤 Standard",
    tagColor: "default",
    icon: <UserOutlined />,
    bgColor: "#f0f5ff",
    borderColor: "#adc6ff",
    titleColor: "#002766",
    shadow: "none"
  },
  REFRACTORY: {
    label: "⛔ Non Réceptif",
    tagColor: "error",
    icon: <StopOutlined />,
    bgColor: "#fff1f0",
    borderColor: "#ff4d4f",
    titleColor: "#cf1322",
    shadow: "0 4px 12px rgba(255, 77, 79, 0.18)"
  }
};

// ⚡ FONCTION D'EXTRACTION DU STATUT DU MÉDECIN
const extractCategory = (p) => {
    if (!p) return 'STANDARD';
    const val = 
        p.relationshipCategory || 
        p.prescriptionProfile || 
        p.prescription || 
        p.profile || 
        p.segment || 
        p.doctor?.prescriptionProfile ||
        p.doctor?.prescription ||
        '';

    const str = String(val).toUpperCase().trim();
    if (str === 'REFRACTORY' || str.includes('RÉCEPTIF') || str.includes('RECEPTIF') || str.includes('REFRACTAIRE')) {
        return 'REFRACTORY';
    }
    if (str === 'PARTNER_AT_RISK' || str.includes('VIGILANCE')) {
        return 'PARTNER_AT_RISK';
    }
    if (str === 'KEY_PRESCRIBER' || str.includes('A+')) {
        return 'KEY_PRESCRIBER';
    }
    if (str === 'PARTNER_ACTIVE' || p.isPartner === true || p.isPartner === 'true' || p.isPartner === 1) {
        return 'PARTNER_ACTIVE';
    }
    return str || 'STANDARD';
};

const MissedVisitsPage = () => {
    const dispatch = useDispatch();
    const isMounted = useRef(true);
    const { user } = useSelector((state) => state.signin);
    const isSupervisor = user?.role?.id === RoleEnum.SUPERVISOR;
    const allCycles = useSelector((state) => state.planning?.cycles || []);
    const reduxDelegates = useSelector((state) => state.delegates?.delegatesList?.data?.data || []);
    const doctorsList = useSelector((state) => state.persons?.doctorsForFilter || []);

    // --- ÉTATS DONNÉES ---
    const [missedVisitsData, setMissedVisitsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [consecutiveMissed, setConsecutiveMissed] = useState([]);
    const [consecutiveCycles, setConsecutiveCycles] = useState(2);
    const [loadingConsecutive, setLoadingConsecutive] = useState(false);

    // ⚡ Profils réels des médecins chargés en direct (sans blocage)
    const [doctorProfiles, setDoctorProfiles] = useState({});

    // --- ÉTATS MODALE PARCOURS MÉDECIN ---
    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);

    const handleOpenDoctorHistory = (personId) => {
        setSelectedDoctorId(personId);
        setDoctorModalVisible(true);
    };

    // --- ÉTATS FILTRES CASCADE ---
    const [selectedStartDate, setSelectedStartDate] = useState(dayjs().subtract(1, 'month').startOf('month'));
    const [selectedEndDate, setSelectedEndDate] = useState(dayjs().endOf('month'));
    const [selectedCycleKeys, setSelectedCycleKeys] = useState([]); 
    const [selectedDelegateIds, setSelectedDelegateIds] = useState([]);
    const [selectedDoctorType, setSelectedDoctorType] = useState('all');
    const [selectedIsPartner, setSelectedIsPartner] = useState('all');
    const [selectedPersonId, setSelectedPersonId] = useState('all');
    const [consecutivePersonFilter, setConsecutivePersonFilter] = useState(null);

    const [delegatesList, setDelegatesList] = useState([]);
    const [loadingDelegates, setLoadingDelegates] = useState(false);
    const [exporting, setExporting] = useState(false);

    // ⚡ 1. DONNÉES FILTRÉES DU TABLEAU (DÉCLARÉ AVANT LES EFFETS)
    const finalTableData = useMemo(() => {
        if (selectedPersonId === 'all') return missedVisitsData;
        return missedVisitsData.filter(item => String(item.person?.id) === String(selectedPersonId));
    }, [missedVisitsData, selectedPersonId]);

    // ⚡ 2. CHARGEMENT CIBLÉ : Uniquement pour les médecins visibles sur la page (1 seule requête pour Tiss Mabrouk !)
    useEffect(() => {
        if (!finalTableData || finalTableData.length === 0) return;

        const visibleIds = finalTableData.slice(0, 20)
            .map(row => row.person?.id)
            .filter(id => id && !doctorProfiles[id]);

        if (visibleIds.length === 0) return;

        visibleIds.forEach(id => {
            axiosInstance.get(`/v1/persons/${id}`)
                .then(res => {
                    const p = res.data?.data || res.data;
                    const cat = extractCategory(p);
                    setDoctorProfiles(prev => ({ ...prev, [id]: cat }));
                })
                .catch(() => {
                    setDoctorProfiles(prev => ({ ...prev, [id]: 'STANDARD' }));
                });
        });
    }, [finalTableData, doctorProfiles]);

    // ⚡ 3. DÉTECTION DE LA COULEUR DE LIGNE (utilisant le profil en direct)
    const getDoctorRowClass = useCallback((person) => {
        if (!person) return '';

        const cat = doctorProfiles[person.id] || extractCategory(person);
        const raw = String(cat).toUpperCase().trim();

        if (raw === 'REFRACTORY' || raw.includes('RÉCEPTIF') || raw.includes('RECEPTIF')) {
            return 'row-refractory-red';
        }
        if (raw === 'PARTNER_AT_RISK' || raw.includes('VIGILANCE')) {
            return 'row-partner-warning';
        }
        if (raw === 'KEY_PRESCRIBER' || raw.includes('A+')) {
            return 'row-prescriber-green';
        }
        if (raw === 'PARTNER_ACTIVE' || person.isPartner) {
            return 'row-partner-gold';
        }
        return '';
    }, [doctorProfiles]);

    // KPIs calculés
    const kpis = useMemo(() => {
        const totalMissed = finalTableData.length;
        const totalRec = consecutiveMissed.length;
        const concernedDelegateIds = new Set();
        finalTableData.forEach(g => g.missedAssignments?.forEach(a => concernedDelegateIds.add(a.delegate.id)));
        return { totalMissed, totalRecidive: totalRec, totalDelegates: concernedDelegateIds.size };
    }, [finalTableData, consecutiveMissed]);

    // Fusion des délégués pour la gamme
    const allKnownDelegates = useMemo(() => {
        const map = new Map();
        [...(reduxDelegates || []), ...(delegatesList || [])].forEach(d => {
            if (d && d.id) {
                const prev = map.get(d.id);
                map.set(d.id, { ...prev, ...d, category: d.category || prev?.category });
            }
        });
        return Array.from(map.values());
    }, [reduxDelegates, delegatesList]);

    const getCategoryTagColor = (cat) => {
        if (!cat) return 'default';
        const c = String(cat).toUpperCase();
        if (c === 'AKTIV') return 'blue';
        if (c === 'SANOSAN') return 'green';
        if (c === 'MATERNITE') return 'purple';
        return 'cyan';
    };

    // Cycles actifs sur la période
    const cycleGroups = useMemo(() => {
        if (!allCycles || allCycles.length === 0 || !selectedStartDate || !selectedEndDate) {
            return [];
        }

        const start = selectedStartDate.startOf('day');
        const end = selectedEndDate.endOf('day');

        const activeCycles = allCycles.filter(cycle => {
            if (!cycle.weeks || cycle.weeks.length === 0) return false;
            const sorted = [...cycle.weeks].sort((a, b) => a.week_number - b.week_number);
            const cStart = dayjs(sorted[0].start_date);
            const cEnd = dayjs(sorted[sorted.length - 1].end_date);
            return cStart.isSameOrBefore(end) && cEnd.isSameOrAfter(start);
        });

        const map = new Map();
        activeCycles.forEach(c => {
            const sorted = [...c.weeks].sort((a, b) => a.week_number - b.week_number);
            const duration = sorted.length;
            const pStart = dayjs(sorted[0].start_date).format('DD/MM');
            const pEnd = dayjs(sorted[sorted.length - 1].end_date).format('DD/MM');
            const groupKey = `${c.name.toUpperCase()}__${duration}W`;

            if (!map.has(groupKey)) {
                map.set(groupKey, {
                    key: groupKey,
                    cycleName: c.name.toUpperCase(),
                    duration: duration,
                    periodStr: `du ${pStart} au ${pEnd}`,
                    cycleIds: [],
                    delegates: []
                });
            }

            const item = map.get(groupKey);
            item.cycleIds.push(c.id);
            if (c.delegate) {
                if (!item.delegates.some(d => d.id === c.delegate.id)) {
                    item.delegates.push(c.delegate);
                }
            }
        });

        return Array.from(map.values()).sort((a, b) => a.duration - b.duration || a.cycleName.localeCompare(b.cycleName));
    }, [allCycles, selectedStartDate, selectedEndDate]);

    const cycleSelectOptions = useMemo(() => {
        return cycleGroups.map(g => ({
            value: g.key,
            label: `${g.cycleName} (Délégués ${g.duration} sem - ${g.periodStr})`,
            duration: g.duration
        }));
    }, [cycleGroups]);

    // Délégués en cascade groupés
    const delegateOptionsGrouped = useMemo(() => {
        const activeGroups = selectedCycleKeys.length > 0 
            ? cycleGroups.filter(g => selectedCycleKeys.includes(g.key))
            : cycleGroups;

        const delegates4W = new Map();
        const delegates3W = new Map();

        activeGroups.forEach(g => {
            g.delegates.forEach(d => {
                const label = `${d.firstname || ''} ${d.lastname || ''}`.trim() || `ID:${d.id}`;
                if (g.duration === 4) {
                    delegates4W.set(d.id, { value: String(d.id), label });
                } else {
                    delegates3W.set(d.id, { value: String(d.id), label });
                }
            });
        });

        const optGroups = [];
        if (delegates4W.size > 0) {
            optGroups.push({
                label: `📅 Délégués avec cycle de 4 semaines (${delegates4W.size})`,
                options: Array.from(delegates4W.values())
            });
        }
        if (delegates3W.size > 0) {
            optGroups.push({
                label: `⚡ Délégués avec cycle de 3 semaines (${delegates3W.size})`,
                options: Array.from(delegates3W.values())
            });
        }

        return optGroups;
    }, [cycleGroups, selectedCycleKeys]);

    const doctorTypeOptions = useMemo(() => [
        { value: 'all', label: 'Tous Types Médecin' },
        { value: '1', label: 'Hospitalier' },
        { value: '2', label: 'Privé' },
    ], []);

    const isPartnerOptions = useMemo(() => [
        { value: 'all', label: 'Tous (Partenaire/Standard)' },
        { value: 'true', label: '⭐ Partenaires' },
        { value: 'false', label: 'Standards' },
    ], []);

    const consecutiveDoctorOptions = useMemo(() => {
        if (!consecutiveMissed) return [];
        return [
            { value: null, label: 'Tous les médecins (Récidives)' }, 
            ...consecutiveMissed.map(item => ({ value: item.person?.id, label: item.person?.name }))
        ];
    }, [consecutiveMissed]);

    const targetedCycleIdsStr = useMemo(() => {
        if (selectedCycleKeys.length === 0) return '';
        const ids = [];
        cycleGroups.forEach(g => {
            if (selectedCycleKeys.includes(g.key)) {
                ids.push(...g.cycleIds);
            }
        });
        return ids.join(',');
    }, [cycleGroups, selectedCycleKeys]);

    const selectedDelegateIdsStr = useMemo(() => {
        return selectedDelegateIds.join(',');
    }, [selectedDelegateIds]);

    const startDateStr = selectedStartDate?.format('YYYY-MM-DD');
    const endDateStr = selectedEndDate?.format('YYYY-MM-DD');

    useEffect(() => {
        isMounted.current = true;
        setLoadingDelegates(true);
        getAllDelegatesList().then(r => { 
            if (!isMounted.current) return;
            setDelegatesList(r?.data?.data || r?.data || []);
        }).finally(() => { if (isMounted.current) setLoadingDelegates(false); });
        
        dispatch(fetchCycles());
        dispatch(getDelegatesList({ page: 1, itemsPerPage: 1000 }));
        dispatch(getAllDoctorsForFilter());
        
        return () => { isMounted.current = false; };
    }, [dispatch]);

    const debounceTimerRef = useRef(null);

    // ⚡ CHARGEMENT INSTANTANÉ DE LA LISTE GLOBALE SANS BLOQUER LE NAVIGATEUR
    const loadAllData = useCallback(() => {
        if (!startDateStr || !endDateStr) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        setLoading(true);
        setLoadingConsecutive(true);

        debounceTimerRef.current = setTimeout(() => {
            const commonParams = {
                startDate: startDateStr,
                endDate: endDateStr,
                delegateIds: selectedDelegateIdsStr || undefined,
                cycleIds: targetedCycleIdsStr || undefined,
                doctorType: selectedDoctorType === 'all' ? undefined : parseInt(selectedDoctorType, 10),
                isPartner: selectedIsPartner === 'all' ? undefined : selectedIsPartner,
            };

            // 1. Liste détaillée instantanée
            getMissedVisitsGrouped(commonParams)
                .then(res => { 
                    if (isMounted.current) {
                        setMissedVisitsData(res?.data || []); 
                    }
                })
                .catch(() => message.error("Erreur lors de la récupération des visites manquées"))
                .finally(() => { if (isMounted.current) setLoading(false); });

            // 2. Alertes récidives
            getConsecutiveMissedVisits({ ...commonParams, consecutiveCycles })
                .then(res => { if (isMounted.current) setConsecutiveMissed(res.data || []); })
                .catch(() => {})
                .finally(() => { if (isMounted.current) setLoadingConsecutive(false); });
        }, 250);
    }, [startDateStr, endDateStr, selectedDelegateIdsStr, targetedCycleIdsStr, selectedDoctorType, selectedIsPartner, consecutiveCycles]);

    useEffect(() => {
        loadAllData();
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [loadAllData]);

    

    // --- COLONNES RÉCIDIVES ---
    const consecutiveColumns = useMemo(() => [
        {
            title: 'Médecin en Récidive',
            key: 'name',
            width: 250,
            render: (_, record) => {
                const docClass = getDoctorRowClass(record.person);
                const isRed = docClass === 'row-refractory-red';
                const isGold = docClass === 'row-partner-gold' || record.person?.isPartner;
                const isWarning = docClass === 'row-partner-warning';
                const isGreen = docClass === 'row-prescriber-green';

                let nameColor = '#1890ff';
                if (isRed) nameColor = '#cf1322';
                else if (isGold) nameColor = '#856404';
                else if (isWarning) nameColor = '#d46b08';
                else if (isGreen) nameColor = '#389e0d';

                return (
                    <Space direction="vertical" size={2}>
                        <Space>
                            {isGold && <StarFilled style={{ color: '#fadb14' }} />}
                            {isRed && <StopOutlined style={{ color: '#cf1322' }} />}
                            <a 
                                onClick={() => handleOpenDoctorHistory(record.person?.id)} 
                                style={{ color: nameColor, fontWeight: 'bold', textDecoration: 'underline' }}
                            >
                                {record.person?.name}
                            </a>
                        </Space>
                        {isRed && (
                            <Tag color="error" style={{ fontSize: '10px', padding: '0 4px', margin: 0, fontWeight: 'bold' }}>
                                ⛔ Non Réceptif
                            </Tag>
                        )}
                        {isWarning && (
                            <Tag color="warning" style={{ fontSize: '10px', padding: '0 4px', margin: 0, fontWeight: 'bold' }}>
                                ⚠️ Vigilance
                            </Tag>
                        )}
                        {isGreen && (
                            <Tag color="success" style={{ fontSize: '10px', padding: '0 4px', margin: 0, fontWeight: 'bold' }}>
                                🚀 A+
                            </Tag>
                        )}
                    </Space>
                );
            }
        },
        { title: 'Spécialité', render: (_, r) => r.person?.specialty || 'N/A', width: 140 },
        { 
            title: 'Délégués concernés', 
            dataIndex: 'missedDelegateDetails', 
            width: 200,
            render: (delegates) => (
                <div>
                    {(delegates || []).map((d, i) => (
                        <div key={i} style={{ marginBottom: 5 }}>
                            <Tag color="orange">{d.delegateName}</Tag>
                            <Badge count={`${d.count} CYCLES`} style={{ backgroundColor: '#f5222d', fontSize: '10px' }} />
                        </div>
                    ))}
                </div>
            )
        },
        { 
            title: 'Détails de la Récidive (Cycles & Durée)', 
            dataIndex: 'missedDelegateDetails', 
            render: (delegates) => (
                <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                    {(delegates || []).map((d, i) => (
                        <div key={i} style={{ padding: '8px 12px', background: '#fafafa', borderRadius: '6px', marginBottom: '8px', border: '1px solid #f0f0f0' }}>
                            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
                                👤 {d.delegateName} :
                            </Text>
                            <List 
                                size="small" 
                                dataSource={d.details} 
                                renderItem={item => (
                                    <List.Item style={{ padding: '2px 0', border: 'none' }}>
                                        <Text style={{ fontSize: '11px' }}>
                                            ⚠️ <b>{item.cycleName}</b> : [{item.period}]
                                        </Text>
                                    </List.Item>
                                )} 
                            />
                        </div>
                    ))}
                </div>
            )
        }
    ], [getDoctorRowClass]);

    // ⚡ 4. COLONNES LISTE DÉTAILLÉE : AFFICHAGE DYNAMIQUE AVEC COULEUR ROUGE ET BADGE
    const missedVisitsColumns = useMemo(() => [
        {
            title: 'Médecin Non Visité',
            dataIndex: ['person', 'name'],
            width: 200,
            fixed: 'left',
            render: (name, record) => {
                const personId = record.person?.id;
                const docClass = getDoctorRowClass(record.person);
                const profile = doctorProfiles[personId] || extractCategory(record.person);
                
                const isRed = docClass === 'row-refractory-red' || profile === 'REFRACTORY';
                const isGold = docClass === 'row-partner-gold' || record.person?.isPartner || profile === 'PARTNER_ACTIVE';
                const isWarning = docClass === 'row-partner-warning' || profile === 'PARTNER_AT_RISK';
                const isGreen = docClass === 'row-prescriber-green' || profile === 'KEY_PRESCRIBER';

                let nameColor = '#1890ff';
                if (isRed) nameColor = '#cf1322';
                else if (isGold) nameColor = '#856404';
                else if (isWarning) nameColor = '#d46b08';
                else if (isGreen) nameColor = '#389e0d';

                return (
                    <Space direction="vertical" size={2}>
                        <Space>
                            {isGold && <StarFilled style={{ color: '#fadb14' }} />}
                            {isRed && <StopOutlined style={{ color: '#cf1322' }} />}
                            <a 
                                onClick={() => handleOpenDoctorHistory(personId)} 
                                style={{ color: nameColor, fontWeight: 'bold', textDecoration: 'underline' }}
                            >
                                {name}
                            </a>
                        </Space>
                        {isRed && (
                            <Tag color="error" style={{ fontSize: '10px', padding: '0 4px', margin: 0, fontWeight: 'bold' }}>
                                ⛔ Non Réceptif
                            </Tag>
                        )}
                        {isWarning && (
                            <Tag color="warning" style={{ fontSize: '10px', padding: '0 4px', margin: 0, fontWeight: 'bold' }}>
                                ⚠️ Vigilance
                            </Tag>
                        )}
                        {isGreen && (
                            <Tag color="success" style={{ fontSize: '10px', padding: '0 4px', margin: 0, fontWeight: 'bold' }}>
                                🚀 A+
                            </Tag>
                        )}
                    </Space>
                );
            },
            sorter: (a, b) => (a.person?.name || '').localeCompare(b.person?.name || ''),
        },
        { title: 'Spécialité', dataIndex: ['person', 'specialtyName'], width: 120 },
        { title: 'Type Médecin', dataIndex: ['person', 'doctorTypeName'], width: 90, render: (text) => <Tag color={text === 'Hospitalier' ? 'blue' : 'green'}>{text}</Tag> },
        { title: 'Région', dataIndex: ['person', 'regionName'], width: 150 },
        {
            title: 'Assignations Manquées (Cycle, Durée & Période Réelle)',
            dataIndex: 'missedAssignments',
            key: 'missedAssignments',
            render: (assignments) => (
                <div style={{ width: '100%' }}>
                    {(assignments || []).map((item, idx) => {
                        const wasVisitedByAnyOther = item.visitedByOthers && item.visitedByOthers.some(o => o.visited);
                        return (
                            <div key={idx} style={{ 
                                padding: '8px 0', 
                                borderBottom: idx !== assignments.length - 1 ? '1px solid #f0f0f0' : 'none',
                                width: '100%' 
                            }}>
                                <Row align="middle" gutter={8}>
                                    <Col flex="150px"><Tag icon={<TeamOutlined />}>{item.delegate.name}</Tag></Col>
                                    <Col flex="160px"><Tag color="geekblue" style={{ fontWeight: 'bold' }}>{item.cycle.name}</Tag></Col>
                                    <Col flex="auto"><Text type="secondary" style={{fontSize: '11px'}}>[{item.cyclePeriod}]</Text></Col>
                                    <Col flex="130px" style={{ textAlign: 'right' }}>
                                        {wasVisitedByAnyOther ? <Tag color="success">Visité autre</Tag> : <Tag color="warning">Non visité autre</Tag>}
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </div>
            )
        },
    ], [getDoctorRowClass, doctorProfiles]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const response = await exportMissedVisits({ 
                startDate: selectedStartDate.format('YYYY-MM-DD'), 
                endDate: selectedEndDate.format('YYYY-MM-DD'), 
                delegateIds: selectedDelegateIdsStr || undefined, 
                cycleIds: targetedCycleIdsStr || undefined,
                isPartner: selectedIsPartner === 'all' ? undefined : selectedIsPartner 
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', `export_manquees.csv`);
            document.body.appendChild(link); link.click();
            message.success("Export terminé");
        } catch (e) { message.error("Erreur lors de l'export"); }
        finally { setExporting(false); }
    };

    const isCalculating = loading || loadingConsecutive;

    return (
        <div style={{ padding: '20px' }}>
            <Title level={2}><WarningOutlined style={{ color: '#faad14', marginRight: 12 }} />Analyse des Visites Manquées</Title>

            <Card style={{ marginBottom: 24, background: '#f0f2f5', border: '1px solid #d9d9d9' }}>
                <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} md={6}>
                        <Text strong><CalendarOutlined /> 1. Période calendaire :</Text>
                        <RangePicker 
                            style={{ width: '100%', marginTop: 4 }} 
                            value={[selectedStartDate, selectedEndDate]} 
                            onChange={d => {
                                setLoading(true);
                                setSelectedStartDate(d?.[0]); 
                                setSelectedEndDate(d?.[1]); 
                                setSelectedCycleKeys([]); 
                                setSelectedDelegateIds([]); 
                                setSelectedPersonId('all');
                            }} 
                            format="DD/MM/YYYY" 
                        />
                    </Col>

                    <Col xs={24} md={8}>
                        <Text strong><ScheduleOutlined /> 2. Cycles actifs (3 sem / 4 sem) :</Text>
                        <Select 
                            mode="multiple" 
                            style={{ width: '100%', marginTop: 4 }} 
                            placeholder={cycleSelectOptions.length > 0 ? "Tous les cycles de la période" : "Aucun cycle trouvé"} 
                            value={selectedCycleKeys} 
                            onChange={(keys) => {
                                setLoading(true);
                                setSelectedCycleKeys(keys);
                                setSelectedDelegateIds([]); 
                            }} 
                            options={cycleSelectOptions} 
                            allowClear
                            maxTagCount="responsive"
                        />
                    </Col>

                    <Col xs={24} md={6}>
                        <Text strong><TeamOutlined /> 3. Délégués concernés :</Text>
                        <Select 
                            mode="multiple" 
                            style={{ width: '100%', marginTop: 4 }} 
                            placeholder="Tous les délégués concernés" 
                            value={selectedDelegateIds} 
                            onChange={(ids) => {
                                setLoading(true);
                                setSelectedDelegateIds(ids);
                            }} 
                            options={delegateOptionsGrouped} 
                            showSearch 
                            optionFilterProp="label" 
                            maxTagCount="responsive"
                            allowClear
                        />
                    </Col>

                    <Col xs={24} md={4}>
                        <Text strong>Partenaire :</Text>
                        <Select 
                            style={{ width: '100%', marginTop: 4 }} 
                            value={selectedIsPartner} 
                            onChange={(v) => {
                                setLoading(true);
                                setSelectedIsPartner(v);
                            }} 
                            options={isPartnerOptions} 
                        />
                    </Col>

                    <Col span={24} style={{ marginTop: 10 }}>
                        <Row gutter={16} align="bottom">
                             <Col span={6}>
                                <Text strong>Type Médecin :</Text>
                                <Select 
                                    style={{ width: '100%', marginTop: 4 }} 
                                    value={selectedDoctorType} 
                                    onChange={(v) => {
                                        setLoading(true);
                                        setSelectedDoctorType(v);
                                    }} 
                                    options={doctorTypeOptions} 
                                />
                             </Col>

                             <Col span={10}>
                                <Text strong>Chercher un médecin dans les résultats :</Text>
                                <Select 
                                    showSearch 
                                    style={{ width: '100%', marginTop: 4 }} 
                                    placeholder="Tous les médecins" 
                                    value={selectedPersonId} 
                                    onChange={setSelectedPersonId} 
                                    options={[{value:'all', label:'Tous les médecins'}, ...missedVisitsData.map(m => ({value: String(m.person?.id), label: m.person?.name}))]} 
                                    optionFilterProp="label" 
                                />
                             </Col>

                             <Col span={8} style={{ textAlign: 'right' }}>
                                <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
                                    Exporter CSV
                                </Button>
                             </Col>
                        </Row>
                    </Col>
                </Row>
            </Card>

            {/* ⚡ ZONE DE RÉSULTATS AVEC LOADING GLOBAL ⚡ */}
            <Spin spinning={isCalculating} size="large" tip="Calcul des visites manquées en cours...">
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col span={8}><Card><Statistic title="Médecins Non Visités" value={kpis.totalMissed} prefix={<UserOutlined />} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                    <Col span={8}><Card><Statistic title="Cas de Récidive" value={kpis.totalRecidive} prefix={<RocketOutlined />} valueStyle={{ color: '#f5222d' }} /></Card></Col>
                    <Col span={8}><Card><Statistic title="Total Délégués Concernés" value={kpis.totalDelegates} prefix={<TeamOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                </Row>

                <Collapse expandIconPosition="end" style={{ marginBottom: 24 }}>
                    <Panel 
                        header={
                            <Space>
                                <RocketOutlined style={{ color: 'red' }} />
                                <Text strong style={{ fontSize: '16px' }}>Alertes Récidives (Cycles Successifs)</Text>
                                <Badge count={consecutiveMissed.length} style={{ backgroundColor: '#f5222d' }} />
                            </Space>
                        } 
                        key="1" 
                        style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: '8px', marginBottom: '16px' }} 
                        extra={
                            <div onClick={e => e.stopPropagation()}>
                                <Space size="large">
                                    <Space>
                                        <Text size="small">Filtrer Médecin :</Text>
                                        <Select 
                                            showSearch 
                                            placeholder="Nom..." 
                                            style={{ width: 220 }} 
                                            size="small" 
                                            optionFilterProp="label" 
                                            value={consecutivePersonFilter} 
                                            onChange={setConsecutivePersonFilter} 
                                            options={consecutiveDoctorOptions} 
                                            allowClear 
                                        />
                                    </Space>
                                    <Space>
                                        <Text size="small">Seuil :</Text>
                                        <Select 
                                            size="small" 
                                            value={consecutiveCycles} 
                                            onChange={(v) => {
                                                setLoadingConsecutive(true);
                                                setConsecutiveCycles(v);
                                            }} 
                                            options={[2,3,4,5].map(v => ({ value: v, label: `${v} cycles` }))} 
                                        />
                                    </Space>
                                </Space>
                            </div>
                        }
                    >
                        <Table 
                            dataSource={consecutivePersonFilter ? consecutiveMissed.filter(item => item.person?.id === consecutivePersonFilter) : consecutiveMissed} 
                            columns={consecutiveColumns} 
                            loading={loadingConsecutive} 
                            pagination={{ pageSize: 5 }} 
                            rowClassName={(record) => getDoctorRowClass(record.person)}
                            size="small" 
                            bordered 
                        />
                    </Panel>

                    <Panel 
                        header={
                            <Space>
                                <UnorderedListOutlined />
                                <Text strong style={{ fontSize: '16px' }}>Liste détaillée de tous les médecins non visités</Text>
                                <Badge count={finalTableData.length} color="blue" />
                            </Space>
                        } 
                        key="2" 
                        style={{ background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '8px' }}
                    >
                        <Table 
                            columns={missedVisitsColumns} 
                            dataSource={finalTableData} 
                            loading={loading} 
                            rowKey={r => r.person?.id || r.id} 
                            rowClassName={(record) => getDoctorRowClass(record.person)}
                            bordered 
                            pagination={{ pageSize: 15 }} 
                            scroll={{ x: 1200 }} 
                        />
                    </Panel>
                </Collapse>
            </Spin>

            {/* ========================================================================= */}
            {/* ⚡ MODALE PARCOURS DE VISITE DU MÉDECIN AVEC COULEUR DYNAMIQUE DU STATUT ⚡ */}
            {/* ========================================================================= */}
             <DoctorHistoryModal 
                visible={doctorModalVisible} 
                onClose={() => setDoctorModalVisible(false)} 
                personId={selectedDoctorId} 
                zIndex={2000}
            />

            {/* STYLES DES LIGNES EN FONCTION DU STATUT DU MÉDECIN */}
            <style>{`
                /* 1. Non Réceptif (ROUGE) - Pour Tiss Mabrouk */
                .ant-table-tbody > tr.row-refractory-red > td,
                .ant-table-tbody > tr.row-refractory-red > td.ant-table-cell,
                .ant-table-tbody > tr.row-refractory-red > td.ant-table-cell-fix-left,
                .ant-table-tbody > tr.row-refractory-red > td.ant-table-cell-fix-left-last,
                tr.row-refractory-red td {
                    background-color: #fff1f0 !important;
                }
                .ant-table-tbody > tr.row-refractory-red:hover > td,
                .ant-table-tbody > tr.row-refractory-red:hover > td.ant-table-cell-fix-left,
                tr.row-refractory-red:hover > td {
                    background-color: #ffccc7 !important;
                }
                .row-refractory-red a,
                .row-refractory-red strong,
                .row-refractory-red .ant-typography {
                    color: #cf1322 !important;
                }

                /* 2. Partenaire Stratégique (OR) */
                .ant-table-tbody > tr.row-partner-gold > td,
                .ant-table-tbody > tr.row-partner-gold > td.ant-table-cell,
                .ant-table-tbody > tr.row-partner-gold > td.ant-table-cell-fix-left,
                .ant-table-tbody > tr.row-partner-gold > td.ant-table-cell-fix-left-last,
                tr.row-partner-gold td {
                    background-color: #fffbe6 !important;
                }
                .ant-table-tbody > tr.row-partner-gold:hover > td,
                .ant-table-tbody > tr.row-partner-gold:hover > td.ant-table-cell-fix-left,
                tr.row-partner-gold:hover > td {
                    background-color: #fff1b8 !important;
                }
                .row-partner-gold a,
                .row-partner-gold strong,
                .row-partner-gold .ant-typography {
                    color: #856404 !important;
                }

                /* 3. Partenaire Sous Vigilance (ORANGE) */
                .ant-table-tbody > tr.row-partner-warning > td,
                .ant-table-tbody > tr.row-partner-warning > td.ant-table-cell,
                .ant-table-tbody > tr.row-partner-warning > td.ant-table-cell-fix-left,
                .ant-table-tbody > tr.row-partner-warning > td.ant-table-cell-fix-left-last,
                tr.row-partner-warning td {
                    background-color: #fff7e6 !important;
                }
                .ant-table-tbody > tr.row-partner-warning:hover > td,
                .ant-table-tbody > tr.row-partner-warning:hover > td.ant-table-cell-fix-left,
                tr.row-partner-warning:hover > td {
                    background-color: #ffe7ba !important;
                }
                .row-partner-warning a,
                .row-partner-warning strong,
                .row-partner-warning .ant-typography {
                    color: #d46b08 !important;
                }

                /* 4. Prescripteur Clé A+ (VERT) */
                .ant-table-tbody > tr.row-prescriber-green > td,
                .ant-table-tbody > tr.row-prescriber-green > td.ant-table-cell,
                .ant-table-tbody > tr.row-prescriber-green > td.ant-table-cell-fix-left,
                .ant-table-tbody > tr.row-prescriber-green > td.ant-table-cell-fix-left-last,
                tr.row-prescriber-green > td {
                    background-color: #f6ffed !important;
                }
                .ant-table-tbody > tr.row-prescriber-green:hover > td,
                .ant-table-tbody > tr.row-prescriber-green:hover > td.ant-table-cell-fix-left,
                tr.row-prescriber-green:hover > td {
                    background-color: #d9f7be !important;
                }
                .row-prescriber-green a,
                .row-prescriber-green strong,
                .row-prescriber-green .ant-typography {
                    color: #389e0d !important;
                }
            `}</style>
        </div>
    );
};

export default MissedVisitsPage;